// Advanced Topic query builder model + compiler (SCRUM-6228).
//
// The builder keeps a UI-friendly tree in Redux (state.search.advancedTopicQuery)
// and compiles it, at search time, into the API contract shape consumed by the
// backend's build_tet_advanced_query (see agr_literature_service search_crud.py):
//
//   internal node : { operator: 'AND'|'OR', children: [node|leaf, ...] }
//   leaf (API)    : { type: 'tet', negate: bool, match: { <short>: [values], ... } }
//
// UI leaves carry an editable `fields` array instead of a `match` object so a
// curator can add/remove sub-facet rows without worrying about object keys; the
// compiler folds that array into the `match` object and prunes empties.

// Entity type is a small controlled vocabulary of ATP curies (there is no search
// aggregation for it), so its dropdown options are static. Mirrors the list used
// by the TET create form (TopicEntityCreate.js) so search values match the curies
// actually stored on tags — a free-text name like "gene" would never match.
export const ENTITY_TYPE_OPTIONS = [
  { value: 'ATP:0000005', label: 'gene (ATP:0000005)' },
  { value: 'ATP:0000006', label: 'allele (ATP:0000006)' },
  { value: 'ATP:0000110', label: 'transgenic allele (ATP:0000110)' },
  { value: 'ATP:0000285', label: 'classical allele (ATP:0000285)' },
  { value: 'ATP:0000123', label: 'species (ATP:0000123)' },
  { value: 'ATP:0000027', label: 'strain (ATP:0000027)' },
  { value: 'ATP:0000025', label: 'genotype (ATP:0000025)' },
  { value: 'ATP:0000026', label: 'fish (ATP:0000026)' },
  { value: 'ATP:0000013', label: 'transgenic construct (ATP:0000013)' },
  { value: 'ATP:0000093', label: 'sequence targeting reagent (ATP:0000093)' },
];

// ATP entity-type curie -> the lowercase entity-type NAME the entity-validation
// endpoint expects (mirrors TopicEntityCreate's curieToNameEntityType). The
// endpoint/resolver collapse the allele and construct variants, so normalize them
// here too. Used to look up a specific Entity (e.g. gene "ACT1") by name -> curie
// via /ontology/entity_validation/{taxon}/{type}/{names} (SCRUM-6228).
const ENTITY_TYPE_CURIE_TO_NAME = {
  'ATP:0000005': 'gene',
  'ATP:0000006': 'allele',
  'ATP:0000110': 'transgenic allele',
  'ATP:0000285': 'classical allele',
  'ATP:0000123': 'species',
  'ATP:0000027': 'strain',
  'ATP:0000025': 'genotype',
  'ATP:0000026': 'fish',
  'ATP:0000013': 'transgenic construct',
  'ATP:0000093': 'sequence targeting reagent',
};
export const entityTypeNameForCurie = (curie) => {
  const name = ENTITY_TYPE_CURIE_TO_NAME[curie] || '';
  if (!name) return '';
  if (name.includes('allele')) return 'allele';
  if (name.includes('construct')) return 'construct';
  return name;
};

// "Has data" is the human-facing view of a tag's boolean `negated` attribute: a
// positive tag (negated=false) asserts the topic HAS data; a negated tag
// (negated=true) asserts NO data. Static Yes/No options so a curator can require, on
// the same tag, e.g. "Topic = disease model AND Source method = acknowledge_form AND
// Has data = yes". BACKEND CONTRACT (build_tet_advanced_query): map the `has_data`
// match key to a term on topic_entity_tags.negated, inverted — "yes" => negated=false,
// "no" => negated=true (it is a boolean field, so there is no .keyword sub-field).
export const HAS_DATA_OPTIONS = [
  { value: 'yes', label: 'yes (has data)' },
  { value: 'no', label: 'no (no data)' },
];

// Professional-biocurator validation of a (usually predicted) tag. A controlled
// vocabulary computed by the backend (calculate_validation_value_for_tag): a
// predicted tag is "validated right"/"validated wrong" when a professional
// biocurator's tag agrees/disagrees with it, "validation conflict" when biocurator
// tags disagree among themselves, "validated right (self)" when the tag itself was
// made by a professional biocurator, and "not validated" when no biocurator tag
// bears on it. Static options (not facet buckets) so the dropdown reads with these
// human labels instead of raw tokens. BACKEND CONTRACT (build_tet_advanced_query):
// the `validation_by_professional_biocurator` match key maps to a term on
// topic_entity_tags.validation_by_professional_biocurator.keyword.
export const VALIDATION_BY_PROFESSIONAL_BIOCURATOR_OPTIONS = [
  { value: 'validated_right', label: 'validated right' },
  { value: 'validated_wrong', label: 'validated wrong' },
  { value: 'validated_right_self', label: 'validated right (self)' },
  { value: 'validation_conflict', label: 'validation conflict' },
  { value: 'not_validated', label: 'not validated' },
];

// key      : short TET sub-facet name -> topic_entity_tags.<key>.keyword on the backend
// label    : display label in the field dropdown
// facetKey : aggregation key in state.search.searchFacets whose buckets seed the
//            value dropdown; null => free-text input (comma-separated for multiple)
// options  : static value dropdown (overrides facetKey/free-text) for controlled vocabs
// range    : confidence score, edited as a [min, max] pair
export const TET_FIELD_DEFS = [
  { key: 'topic', label: 'Topic', facetKey: 'topics' },
  { key: 'entity_type', label: 'Entity type', facetKey: null, options: ENTITY_TYPE_OPTIONS },
  { key: 'entity', label: 'Entity', facetKey: null },
  { key: 'source_method', label: 'Source method', facetKey: 'source_methods' },
  { key: 'source_evidence_assertion', label: 'Source evidence assertion', facetKey: 'source_evidence_assertions' },
  { key: 'confidence_level', label: 'Confidence level', facetKey: 'confidence_levels' },
  { key: 'data_novelty', label: 'Data novelty', facetKey: 'data_novelty' },
  {
    key: 'validation_by_professional_biocurator',
    label: 'Validation (biocurator)',
    facetKey: null,
    options: VALIDATION_BY_PROFESSIONAL_BIOCURATOR_OPTIONS,
  },
  { key: 'has_data', label: 'Has data', facetKey: null, options: HAS_DATA_OPTIONS },
  { key: 'species', label: 'Species', facetKey: null },
  { key: 'confidence_score', label: 'Confidence score', range: true },
];

export const FIELD_DEF_BY_KEY = TET_FIELD_DEFS.reduce((acc, def) => {
  acc[def.key] = def;
  return acc;
}, {});

// Workflow-tag conditions in the same tree (SCRUM-6398). A Workflow card is a
// leaf of type 'wft' carrying a single field row (workflow_tag_id) whose chip
// values are ATP workflow-tag curies; values OR within the card, cards combine
// with the tree operator alongside Topic Tag cards. The value dropdown is
// grouped by the same workflow categories as the facet panel — each key is a
// search aggregation whose buckets seed that group's options. BACKEND CONTRACT
// (build_tet_advanced_query): a {type:'wft', match:{workflow_tag_id:[...]}}
// leaf compiles to a nested workflow_tags query, MOD-scoped like the flat
// workflow facet path.
export const WFT_CATEGORY_DEFS = [
  { key: 'file_workflow', label: 'File workflow' },
  { key: 'reference_classification', label: 'Reference classification' },
  { key: 'entity_extraction', label: 'Entity extraction' },
  { key: 'manual_indexing', label: 'Manual indexing' },
  { key: 'curation_classification', label: 'Curation classification' },
  { key: 'community_curation', label: 'Community curation' },
  { key: 'first_pass_curation', label: 'First pass curation' },
  { key: 'email_extraction', label: 'Email extraction' },
];

export const isRangeField = (key) => !!(FIELD_DEF_BY_KEY[key] && FIELD_DEF_BY_KEY[key].range);

// Factory helpers — every new node is a fresh object so React state updates stay immutable.
// A field row now carries a `values` array of { value, label } chips (multiple
// values on one field = OR); `label` is display-only, the compiler reads `value`.
// Range fields keep min/max. (Legacy rows with a scalar `value` still compile.)
// A new field row starts empty, except "Has data": adding it pre-selects
// "yes (has data)" so an explicit Has-data field is a ready-to-use control (a
// curator can switch it to "no"). The tree-wide "exclude no-data" toggle can also
// apply has_data = yes to every positive tag, but is off by default — see
// EXCLUDE_NO_DATA_DEFAULT and compileAdvancedQuery (SCRUM-6228).
export const createFieldRow = (field = 'topic') => ({
  field,
  values: field === 'has_data'
    ? [{ value: HAS_DATA_OPTIONS[0].value, label: HAS_DATA_OPTIONS[0].label }]
    : [],
  min: 0,
  max: 1,
});
export const createLeaf = () => ({ type: 'tet', negate: false, fields: [createFieldRow()] });
// A Workflow condition reuses the leaf/fields row plumbing with a single fixed
// field, so chips, compile, dedupe and save/restore all work unchanged (SCRUM-6398).
export const createWftLeaf = () => ({
  type: 'wft',
  negate: false,
  fields: [{ field: 'workflow_tag_id', values: [] }],
});
export const createGroup = () => ({ operator: 'OR', children: [createLeaf()] });
// The Tag-card UI keeps a FLAT tree: leaves (one per Tag) directly under the root,
// combined with the top-level operator. The compiler still supports nested groups,
// so older/nested saved trees round-trip; normalizeToFlatTree collapses them for
// display when the flat builder loads one.
//
// excludeNoData optionally mirrors the facet search's "exclude negative": when on,
// every positive tag condition is compiled with has_data = yes so results only
// include tags that have data. It is surfaced as a single toggle (not a per-Tag
// field) and shown in the query preview; checking it, or adding an explicit Has data
// field to a Tag, applies it. It defaults OFF: forcing has_data = yes by default hid
// the no-data tags that some MODs (e.g. FB) rely on, breaking their searches
// (SCRUM-6228).
export const EXCLUDE_NO_DATA_DEFAULT = false;
export const createEmptyTree = () => ({
  operator: 'AND',
  excludeNoData: EXCLUDE_NO_DATA_DEFAULT,
  children: [createLeaf()],
});

export const isLeaf = (node) => !!node && (node.type === 'tet' || node.type === 'wft');
export const isWftLeaf = (node) => !!node && node.type === 'wft';

// Collapse duplicate field rows within a leaf so the one-row-per-field invariant
// the flat Tag builder relies on holds for restored queries too. A query saved
// before that invariant (or a nested tree) can carry the same field twice; the
// builder would then render two rows of the same type, and Add-field could be
// disabled while types remain unused. Later duplicates merge their chip values
// into the first row (values on one field are OR, so nothing is lost); range
// fields keep the first row's min/max. First-occurrence order is preserved.
const dedupeLeafFields = (leaf) => {
  if (!leaf || !Array.isArray(leaf.fields)) return leaf;
  const byField = new Map();
  leaf.fields.forEach((row) => {
    if (!row || !row.field) return;
    const existing = byField.get(row.field);
    if (!existing) {
      byField.set(row.field, { ...row });
      return;
    }
    const valueKey = (v) => (v && typeof v === 'object' ? v.value : v);
    const seen = new Set((existing.values || []).map(valueKey));
    const merged = [...(existing.values || [])];
    (row.values || []).forEach((v) => {
      const k = valueKey(v);
      if (k && !seen.has(k)) { seen.add(k); merged.push(v); }
    });
    existing.values = merged;
  });
  return { ...leaf, fields: Array.from(byField.values()) };
};

// Collect every leaf in a (possibly nested) tree into a flat { operator, children:[leaf] }
// so the Tag-card builder can display a legacy/nested saved query without crashing.
// Best-effort: intermediate group operators are dropped (the flat UI can't show them).
export const normalizeToFlatTree = (tree) => {
  if (!tree) return createEmptyTree();
  const leaves = [];
  const walk = (n) => {
    if (!n) return;
    if (isLeaf(n)) { leaves.push(n); return; }
    (n.children || []).forEach(walk);
  };
  walk(tree);
  return {
    operator: String(tree.operator || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND',
    // Preserve an explicit excludeNoData; default it off for trees that predate the flag.
    excludeNoData: tree.excludeNoData === true,
    children: leaves.length > 0 ? leaves.map(dedupeLeafFields) : [createLeaf()],
  };
};

// True when a (possibly restored) tree must be run through normalizeToFlatTree
// before the flat Tag builder can safely display it. Three cases:
//   - it still contains nested groups (the flat UI can't render them),
//   - it predates the excludeNoData flag (undefined reads as an unset default),
//   - it carries duplicate field rows within a Tag — a search saved by the flat
//     builder AFTER excludeNoData landed but BEFORE the one-row-per-field
//     invariant. Those trees are all-leaf with excludeNoData defined, so the first
//     two checks miss them; without this the duplicates survive and Add-field can
//     be disabled while field types remain unused (SCRUM-6333).
// Idempotent with normalizeToFlatTree: a normalized tree returns false, so the
// builder's one-shot seeding effect can't loop.
export const needsFlatNormalization = (tree) => {
  if (!tree) return false;
  const children = tree.children || [];
  if (children.some((c) => !isLeaf(c))) return true;
  if (tree.excludeNoData === undefined) return true;
  return children.some((c) => {
    const fieldKeys = (c.fields || []).map((f) => f && f.field).filter(Boolean);
    return new Set(fieldKeys).size !== fieldKeys.length;
  });
};

// Split a free-text value into a trimmed, de-duplicated, non-empty list.
const splitValues = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  return String(value ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
};

// Pull the string values out of a field row, accepting both the current chip
// shape (`values: [{ value, label } | string]`) and the legacy scalar `value`
// (comma-separated). Trimmed, de-duplicated, non-empty.
const rowValues = (row) => {
  if (Array.isArray(row.values)) {
    return splitValues(row.values.map((v) => (v && typeof v === 'object' ? v.value : v)));
  }
  return splitValues(row.value);
};

// Fold a UI leaf's `fields` array into an API `match` object. Returns null when
// no field contributes a value (so the leaf is dropped by the caller).
const compileLeafMatch = (leaf) => {
  const match = {};
  (leaf.fields || []).forEach((row) => {
    if (!row || !row.field) return;
    if (isRangeField(row.field)) {
      const min = Number(row.min);
      const max = Number(row.max);
      if (Number.isFinite(min) && Number.isFinite(max)) {
        match.confidence_score = [min, max];
      }
      return;
    }
    const values = rowValues(row);
    if (values.length === 0) return;
    // Merge when the same sub-facet appears in more than one row (values OR within a field).
    match[row.field] = (match[row.field] || []).concat(values);
  });
  return Object.keys(match).length > 0 ? match : null;
};

// Compile the UI tree into the API contract shape. Empty leaves/groups collapse
// away; a node with a single effective child returns that child directly;
// returns null when the whole tree is empty (caller then omits tet_advanced_query).
//
// When the tree's excludeNoData flag is on, each leaf that does not already pin
// has_data gets has_data = yes injected, so only tags that have data count — the
// same default the facet search applies as "exclude negative". This applies to
// excluded (negated) leaves too: NOT (topic AND has_data = yes) drops a paper
// only when a DATA-carrying tag matches, while a paper whose only matching tag
// is no-data is kept, consistent with "no-data tags don't count" (SCRUM-6400;
// previously negated leaves were skipped and the clause silently vanished).
// The flag lives on the tree root; `exclude` propagates it to leaves. A leaf
// carrying an explicit Has data field overrides the default (SCRUM-6228).
export const compileAdvancedQuery = (node, exclude) => {
  if (!node) return null;
  const excludeNoData = exclude === undefined ? node.excludeNoData === true : exclude;
  if (isLeaf(node)) {
    const match = compileLeafMatch(node);
    if (!match) return null;
    // has_data is an attribute of topic-entity tags only; a Workflow condition
    // (wft leaf) has no polarity, so the excludeNoData default never touches it.
    if (excludeNoData && !isWftLeaf(node) && !('has_data' in match)) {
      match.has_data = ['yes'];
    }
    return { type: node.type, negate: !!node.negate, match };
  }
  const children = (node.children || [])
    .map((child) => compileAdvancedQuery(child, excludeNoData))
    .filter(Boolean);
  if (children.length === 0) return null;
  if (children.length === 1) return children[0];
  return { operator: String(node.operator || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND', children };
};

// True when the tree would produce no query (used to decide whether a search is
// runnable). The excludeNoData default never makes an otherwise-empty tree runnable:
// has_data is only injected into leaves that already have a value.
export const isAdvancedQueryEmpty = (node) => compileAdvancedQuery(node) === null;

// Build a (field, value) -> display-name lookup from a UI tree's chips, so a
// preview reads "topic = \"disease model\"" rather than showing the raw curie.
// Falls back to the raw value for anything not carrying a label (SCRUM-6228).
export const buildValueLabeler = (tree) => {
  const map = {};
  const walk = (n) => {
    if (!n) return;
    if (isLeaf(n)) {
      (n.fields || []).forEach((f) =>
        (Array.isArray(f.values) ? f.values : []).forEach((v) => {
          if (v && typeof v === 'object') map[`${f.field}::${v.value}`] = v.label;
        }));
    }
    (n.children || []).forEach(walk);
  };
  walk(tree);
  return (field, value) => map[`${field}::${value}`] || value;
};

// Render a COMPILED query as a readable, SQL-like preview. Built from the compiled
// output (not the UI tree) so what a curator reads matches exactly what is sent to
// the backend. `labelFor(field, value)` supplies display names for curie values;
// it falls back to the raw value. Same-field value lists render as `in (...)` (OR),
// distinct fields on one tag as `AND`, tags/groups per the node operator.
export const describeCompiledQuery = (node, labelFor = (_f, v) => v) => {
  if (!node) return '';
  if (node.type === 'tet' || node.type === 'wft') {
    const parts = Object.entries(node.match).map(([field, vals]) => {
      if (field === 'confidence_score') {
        return `confidence_score in [${vals[0]}, ${vals[1]}]`;
      }
      // The wft leaf's only field reads better as "workflow_tag" than the raw key.
      const shownField = field === 'workflow_tag_id' ? 'workflow_tag' : field;
      const shown = (vals || []).map((v) => `"${labelFor(field, v)}"`);
      return shown.length === 1
        ? `${shownField} = ${shown[0]}`
        : `${shownField} in (${shown.join(', ')})`;
    });
    const body = parts.join(' AND ');
    return node.negate ? `NOT (${body})` : `(${body})`;
  }
  const op = String(node.operator).toUpperCase() === 'OR' ? ' OR ' : ' AND ';
  return `(${node.children.map((c) => describeCompiledQuery(c, labelFor)).join(op)})`;
};

// Grid filter keys for the Topic grid's /topic_entity_tag/by_references endpoint,
// which takes a FLAT filter object (it cannot express an arbitrary AND/OR tree).
const GRID_POSITIVE_KEYS = {
  topic: 'topics',
  confidence_level: 'confidence_levels',
  source_method: 'source_methods',
  source_evidence_assertion: 'source_evidence_assertions',
  data_novelty: 'data_novelty',
  entity_type: 'entity_types',
  entity: 'entities',
};
const GRID_NEGATED_KEYS = {
  confidence_level: 'negated_confidence_levels',
  source_method: 'negated_source_methods',
  source_evidence_assertion: 'negated_source_evidence_assertions',
};

// Best-effort FLAT grid filter from a COMPILED advanced tree (SCRUM-6228). The
// grid's flat filter can't model the builder's boolean tree, and row inclusion is
// already authoritative from the search. So we UNION every leaf's values per
// sub-facet (the grid shows any tag matching any leaf) and never claim single-tag
// semantics. Sub-facets without a grid-filter equivalent (species) are dropped —
// they still constrained the search, just not the grid's tag highlighting.
// Returns undefined when nothing maps (grid then fetches all tags per reference).
export const flattenAdvancedForGrid = (compiled) => {
  if (!compiled) return undefined;
  const out = {};
  const push = (key, values) => {
    const cur = out[key] || [];
    (Array.isArray(values) ? values : [values]).forEach((v) => {
      if (!cur.includes(v)) cur.push(v);
    });
    out[key] = cur;
  };
  let scoreMin = null;
  let scoreMax = null;
  const walk = (node) => {
    if (!node) return;
    // Workflow conditions constrain the search but have no TET-grid equivalent;
    // skip them like the other unmapped sub-facets (SCRUM-6398).
    if (node.type === 'wft') return;
    if (node.type === 'tet') {
      const keys = node.negate ? GRID_NEGATED_KEYS : GRID_POSITIVE_KEYS;
      Object.entries(node.match || {}).forEach(([field, values]) => {
        if (field === 'confidence_score') {
          if (!node.negate && Array.isArray(values)) {
            scoreMin = scoreMin === null ? values[0] : Math.min(scoreMin, values[0]);
            scoreMax = scoreMax === null ? values[1] : Math.max(scoreMax, values[1]);
          }
          return;
        }
        if (keys[field]) push(keys[field], values);
      });
      return;
    }
    (node.children || []).forEach(walk);
  };
  walk(compiled);
  // Union of leaf ranges; omit when it's the full default range [0, 1].
  if (scoreMin !== null && scoreMax !== null && !(scoreMin === 0 && scoreMax === 1)) {
    out.confidence_score_min = scoreMin;
    out.confidence_score_max = scoreMax;
  }
  return Object.keys(out).length > 0 ? out : undefined;
};
